import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../../../environments/environment';
import { JourneyLogContextService } from './journey-log-context.service';

export type AppLogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
export type AppLogOutcome = 'SUCCESS' | 'FAILURE' | 'RETRY' | 'CANCELLED' | 'IN_PROGRESS' | 'STARTED';

export interface AppLoggerOptions {
  event: string;
  component: string;
  message: string;
  level?: AppLogLevel;
  securityEvent?: string;
  userIdentity?: string;
  requestId?: string;
  correlationId?: string;
  route?: string;
  outcome?: AppLogOutcome;
  error?: unknown;
  meta?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class AppLoggerService {
  private readonly context = inject(JourneyLogContextService);
  private readonly logger = inject(NGXLogger);

  info(options: AppLoggerOptions): void {
    this.emit('INFO', options);
  }

  debug(options: AppLoggerOptions): void {
    this.emit('DEBUG', options);
  }

  warn(options: AppLoggerOptions): void {
    this.emit('WARN', options);
  }

  error(options: AppLoggerOptions): void {
    this.emit('ERROR', options);
  }

  private emit(level: AppLogLevel, options: AppLoggerOptions): void {
    const payload = {
      timestamp: this.formatUtcTimestamp(),
      severity: level,
      level,
      event: options.event,
      message: options.message,
      app: 'spark-my-profile',
      env: environment.production ? 'prod' : 'dev',
      component: options.component,
      route: options.route ?? this.context.getRoute(),
      securityEvent: options.securityEvent ?? 'GENERAL_APPLICATION_EVENT',
      userIdentity: options.userIdentity ?? this.context.getUserIdentity(),
      correlationId: options.correlationId ?? this.context.getCorrelationId() ?? 'UNKNOWN_CORRELATION',
      requestId: options.requestId ?? 'req-none',
      outcome: options.outcome ?? 'SUCCESS',
      error: this.normalizeError(options.error),
      meta: options.meta ?? {},
    };

    const serialized = JSON.stringify(payload);

    if (level === 'ERROR') {
      this.logger.error(serialized);
      return;
    }

    if (level === 'WARN') {
      this.logger.warn(serialized);
      return;
    }

    if (level === 'DEBUG') {
      this.logger.debug(serialized);
      return;
    }

    this.logger.info(serialized);
  }

  private normalizeError(error: unknown): unknown {
    if (!error) {
      return null;
    }

    if (error instanceof Error) {
      return { name: error.name, message: error.message };
    }

    return error;
  }

  private formatUtcTimestamp(): string {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hour = String(now.getUTCHours()).padStart(2, '0');
    const minute = String(now.getUTCMinutes()).padStart(2, '0');
    const second = String(now.getUTCSeconds()).padStart(2, '0');

    return `${year}${month}${day}-${hour}${minute}${second}`;
  }
}
